import {
  Quaternion,
  QuaternionTuple,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import {
  PathNavigationCapability,
  FrustumCapability,
  NavigationalCapabilityBase,
  StationsNavigationCapability,
  findClosestStation,
} from '@trimble/web3d-capabilities';
import {
  getTrbModelId,
  StationPathIndexProvider,
  StationsCapabilityProvider,
} from '@trimble/web3d-providers';
import {
  Point2D,
  Point3D,
  arePointsEqual,
  partialPointToVector3,
} from '@trimble/utilities';
import {
  CapabilityRegistryContainer,
  NavigationCapabilityCoordinator,
} from '@trimble/web3d';
import type { SyncPosition } from './sync-position.types';

export function getActiveNavCap(
  pane: CapabilityRegistryContainer
): NavigationalCapabilityBase | undefined {
  return pane
    .get(NavigationCapabilityCoordinator)()
    ?.activeNavigationCapability();
}

const NDC_TO_SCREEN_DIVISOR = 2;

export function isPointVisibleInMap(
  point: Vector3,
  viewer: Web3DViewer
): boolean {
  const matrixWorldInverse = viewer.camera.matrixWorld.clone().invert();
  const projectionMatrix = viewer.camera.projectionMatrix.clone();
  const mapPoint = new Vector3(point.x, point.y, 0);

  const pos = mapPoint.clone();
  pos.applyMatrix4(matrixWorldInverse);
  pos.applyMatrix4(projectionMatrix);

  const screenX = ((pos.x + 1) * viewer.clientWidth) / NDC_TO_SCREEN_DIVISOR;
  const screenY = ((pos.y + 1) * viewer.clientHeight) / NDC_TO_SCREEN_DIVISOR;

  return !(
    screenX < 0 ||
    screenX > viewer.clientWidth ||
    screenY < 0 ||
    screenY > viewer.clientHeight
  );
}

export function updateFrustum(
  targetPane: CapabilityRegistryContainer,
  targetPosition: Point3D | Point2D | Vector3 | null,
  quaternion: QuaternionTuple | null,
  fov: number | null
): void {
  const frustumCap = targetPane.get(FrustumCapability)();
  frustumCap?.setFrustumPosition({ targetPosition, quaternion, fov });
}

export function normalizedPathId(
  alignmentModelId: string | undefined
): string | undefined {
  if (!alignmentModelId) return undefined;
  return getTrbModelId(alignmentModelId) ?? alignmentModelId;
}

/**
 * Navigates the target to the station that matches the sync, skipping the
 * navigation when the camera is already there. Without an index or a source
 * mission, falls back to the station closest in XY.
 * Shared between station→station and alignment→station sync.
 */
export function navigateToSyncStation(
  sync: SyncPosition,
  targetNavCap: StationsNavigationCapability,
  stationsProvider: StationsCapabilityProvider,
  index: StationPathIndexProvider | undefined,
  targetViewer: { camera: { position: Vector3 } }
): void {
  const stationIndex =
    index && sync.missionId
      ? resolveMissionStationIndex(sync, sync.missionId, index)
      : findClosestStation(
          partialPointToVector3(sync.position),
          stationsProvider.stationsInfo().map((s) => s.position)
        )?.index;
  if (stationIndex === undefined) return;

  const position = stationsProvider.stationsInfo()[stationIndex]?.position;
  if (position && arePointsEqual(targetViewer.camera.position, position)) {
    return;
  }
  targetNavCap.goToStation(stationIndex);
}

/** Same station, else the station at the path distance, else the 3D-nearest one of the mission. */
function resolveMissionStationIndex(
  sync: SyncPosition,
  missionId: string,
  index: StationPathIndexProvider
): number | undefined {
  const sameStation = sync.stationKey
    ? index.getById(sync.stationKey)
    : undefined;
  if (sameStation) return sameStation.stationIndex;

  const atDistance =
    sync.pathId && sync.pathDistance !== undefined
      ? index.closestStationAtDistance(sync.pathId, sync.pathDistance)
      : undefined;
  if (atDistance?.missionId === missionId) return atDistance.stationIndex;

  return index.closestStationInMission(missionId, sync.position)?.stationIndex;
}

export function isPathOfOtherMission(
  sync: SyncPosition,
  targetCap: PathNavigationCapability,
  index: StationPathIndexProvider | undefined
): boolean {
  const targetPathId = normalizedPathId(targetCap.alignmentModelId());
  const targetMissionId = targetPathId
    ? index?.missionOfPath(targetPathId)
    : undefined;
  return (
    sync.missionId !== undefined &&
    targetMissionId !== undefined &&
    targetMissionId !== sync.missionId
  );
}

interface FollowedStation {
  readonly stationPane: CapabilityRegistryContainer;
  readonly stationKey: string | undefined;
  readonly pathDistance: number;
}

const followedStations = new WeakMap<
  PathNavigationCapability,
  FollowedStation
>();

/** Records the station pane and station the path pane's marker now follows. */
export function followStation(
  pathCap: PathNavigationCapability,
  stationPane: CapabilityRegistryContainer,
  stationKey: string | undefined
): void {
  const pathDistance = pathCap.distance();
  if (pathDistance === undefined) {
    followedStations.delete(pathCap);
    return;
  }
  followedStations.set(pathCap, { stationPane, stationKey, pathDistance });
}

/**
 * The station the path marker still follows from `stationPane`, else null.
 * A camera-only change of the path pane (the tool eases its orientation after
 * each move) would echo that station back, over a newer one picked meanwhile.
 */
export function getFollowedStation(
  pathCap: PathNavigationCapability,
  stationPane: CapabilityRegistryContainer,
  sync: SyncPosition
): FollowedStation | null {
  const followed = followedStations.get(pathCap);
  if (!followed) return null;
  // Once the marker leaves it, a return to the same distance is a path move.
  if (followed.pathDistance !== sync.pathDistance) {
    followedStations.delete(pathCap);
    return null;
  }
  return followed.stationPane === stationPane ? followed : null;
}

/**
 * Navigates the target path pane to the sync. On the source's own path it
 * goes by path distance, which stays on the right lap; on another mission's
 * path it stays.
 */
export async function navigateAlongPath(
  sync: SyncPosition,
  targetCap: PathNavigationCapability,
  index: StationPathIndexProvider | undefined,
  quaternion?: Quaternion
): Promise<void> {
  if (isPathOfOtherMission(sync, targetCap, index)) return;
  const targetPathId = normalizedPathId(targetCap.alignmentModelId());
  if (sync.pathDistance !== undefined && targetPathId === sync.pathId) {
    await targetCap.navigateToPosition(
      { distance: sync.pathDistance },
      quaternion
    );
    return;
  }
  await targetCap.navigateToWorldPosition(sync.position, quaternion);
}
